import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { DayOfWeek } from '@askporter/client-grieg-lyric';
import { Button } from '../../../../Buttons/Button';
import { SelectDayTimeModal } from '../../../../DayTimeAndRange/DayTime/components/SelectDayTimeModal';
import { DayTimeType, DayTimeState } from '../../../../DayTimeAndRange/DayTime/types';
import {
  defaultDays,
  parseDayTime,
  selectAllDays,
  sortDays,
  reducer,
  days,
  getAllDayTimes,
} from '../../../../DayTimeAndRange/DayTime/utils';
import { Typography } from '../../../../Typography';

export interface CleaningScheduleGridProps {
  /**
   * Translation function.
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * Whether we are on a small-screen device (forwarded to the edit modal).
   */
  isSmallDevice: boolean;
  /**
   * The current DAYTIME schedule value.
   */
  value?: DayTimeType[];
  /**
   * Called with the updated schedule whenever the user saves an edit in the modal.
   */
  onChange: (value: DayTimeType[]) => void;
  /**
   * When true the grid is display-only — cells are not clickable and the bulk-edit
   * controls / modal are not rendered.
   */
  disabled?: boolean;
}

const CELL_ACTIVE = { bg: '#ECE6F5', border: '#D5C8EB', text: '#410099' };
const CELL_EMPTY = { bg: '#FFFFFF', border: '#EAECF0', text: '#98A2B3' };

/**
 * A week-at-a-glance schedule grid for the cleaning management form. Displays one
 * cell per weekday and — when editable — reuses the shared `SelectDayTimeModal`
 * (day toggles + time picker) so editing stays consistent with the rest of the app.
 */
export const CleaningScheduleGrid: React.FC<CleaningScheduleGridProps> = ({
  t,
  isSmallDevice,
  value,
  onChange,
  disabled = false,
}: CleaningScheduleGridProps) => {
  const parsedDayTimes = parseDayTime(getAllDayTimes(value || []));

  const initialState: DayTimeState = {
    isVisible: false,
    selectedDays: defaultDays,
    dayTimes: parsedDayTimes,
    timeValue: null,
    noServiceCheckbox: false,
  };

  const [{ isVisible, selectedDays, dayTimes, timeValue, noServiceCheckbox }, dispatch] = React.useReducer(
    reducer,
    initialState,
  );

  useEffect(() => {
    dispatch({ type: 'addDayTime', payload: parseDayTime(getAllDayTimes(value || [])) });
  }, [value]);

  const orderedDays = Object.keys(dayTimes || {}).sort(sortDays) as DayOfWeek[];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
        {orderedDays.map((day) => {
          const time = dayTimes[day];
          const palette = time ? CELL_ACTIVE : CELL_EMPTY;
          const shortLabel = t(`common:day_of_week:${day}`)?.substring(0, 3).toUpperCase();

          const editDay = () =>
            dispatch({
              type: 'selectDay',
              payload: {
                selectedDays: { ...defaultDays, [days[day]]: true },
                timeValue: time || '',
              },
            });

          return (
            <Box
              key={day}
              role={disabled ? undefined : 'button'}
              tabIndex={disabled ? undefined : 0}
              aria-label={disabled ? undefined : t(`day_time:edit:${day}`)}
              onClick={disabled ? undefined : editDay}
              onKeyDown={
                disabled
                  ? undefined
                  : (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        editDay();
                      }
                    }
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                height: '84px',
                boxSizing: 'border-box',
                borderRadius: '8px',
                border: `1px solid ${palette.border}`,
                backgroundColor: palette.bg,
                cursor: disabled ? 'default' : 'pointer',
                ...(disabled ? {} : { '&:hover': { borderColor: '#410099' } }),
              }}
            >
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', letterSpacing: '0.4px' }}>
                {shortLabel}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: palette.text }}>{time || '—'}</Typography>
            </Box>
          );
        })}
      </Box>

      {!disabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', mt: '10px' }}>
          <Typography sx={{ flex: 1, fontSize: '12px', color: '#667085', minWidth: '160px' }}>
            {t('day_time:edit:click_to_edit', { defaultValue: 'Click a day to edit its times' })}
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => dispatch({ type: 'selectAllDays', payload: selectAllDays })}
          >
            {t('day_time:edit:all_hours')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() =>
              dispatch({
                type: 'selectMonFri',
                payload: { ...defaultDays, 0: true, 1: true, 2: true, 3: true, 4: true },
              })
            }
          >
            {t('day_time:edit:mon_fri')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => dispatch({ type: 'selectSatSun', payload: { ...defaultDays, 5: true, 6: true } })}
          >
            {t('day_time:edit:sat_sun')}
          </Button>
        </Box>
      )}

      {!disabled && (
        <SelectDayTimeModal
          selectedDays={selectedDays}
          title={t('day_time:select_day_time:title')}
          t={t}
          isSmallDevice={isSmallDevice}
          isVisible={isVisible}
          setIsModalVisible={(nextVisible: boolean) => dispatch({ type: 'setModalVisibility', payload: nextVisible })}
          handleClose={() => dispatch({ type: 'setModalVisibility', payload: false })}
          onAdd={onChange}
          dataTestId="cleaning-schedule-day-time-modal"
          value={value || []}
          setTimeValue={(nextTimeValue: string | null) => dispatch({ type: 'setTimeValue', payload: nextTimeValue })}
          timeValue={timeValue}
          setNoServiceCheckbox={(nextNoService: boolean) =>
            dispatch({ type: 'setNoServiceCheckbox', payload: nextNoService })
          }
          noServiceCheckbox={noServiceCheckbox}
          setSelectedDays={(nextSelectedDays: Record<DayOfWeek, boolean>) =>
            dispatch({ type: 'setSelectedDays', payload: nextSelectedDays })
          }
          addDayTime={(dayTimesPayload: Record<DayOfWeek, string | null>) =>
            dispatch({ type: 'addDayTime', payload: dayTimesPayload })
          }
          dayTimes={dayTimes}
        />
      )}
    </>
  );
};
